// Sidecar implementation ofthe CpaasPlatformUserService
// Instead of providing this service in Root, we'll provide it in the sidecar app

import { Injectable, inject } from '@angular/core';
import { CPAAS_CUSTOMER_DETAILS_FIELDS, CpaasCustomerService, TnOrderAccountInfo } from '@nuso/cpaas/data-access';
import { CpaasCustomerSummary, CpaasCustomer, TnPortalType } from '@nuso/cpaas/types';
import { ServicesService } from '@nuso/portal/data-access-services';
import { Observable, map, filter, of} from 'rxjs';
import { isDefined } from '@nuso/common/util-ts';

import { GraphApiService } from '@nuso/common/data-access-api';
import { normalizePlatformServices, platformServiceFields } from '@nuso/services/data-access';
import { gql } from 'graphql-tag';

export const cpaasCustomerDetailsQuery = gql`
  query cpaasCustomerDetails($input: CpaasCustomerDetailsInput!) {
    cpaasCustomerDetails(input: $input) {
      ${CPAAS_CUSTOMER_DETAILS_FIELDS}
      services {
        ${platformServiceFields}
      }
    }
  }
`;

@Injectable()
export class PortalCpaasCustomerService extends CpaasCustomerService {
  private graphApi = inject(GraphApiService);
  private servicesService = inject(ServicesService);


  public cpaasCustomerSummaries(): Observable<CpaasCustomerSummary[]> {
    return this.servicesService.currentCustomer().pipe(
      filter(isDefined),
      map(customer => {
        return [customer, ...customer.descendants || []].map(c => ({
          id: c.id,
          name: c.name,
          level: 3
        }));
      }),
    );
  }

  /*
  * Note: we are using network-only fetchPolicy here because we need to ensure that we get the latest data from the server.
  * This is needed to handle us added new services or updating services, then coming in and ordering numbers.
  * If we use cache-and-network, there is a wild condition, where the observable returns twice and causes us to
  * call the initialize followed by the update in the components. To test, load an order details page, then
  * go to the orders page, then click on one of the details, and you will see the issue, where the service/trunks are blanked out.
  */
  public cpaasCustomerDetails(customerId: number): Observable<CpaasCustomer | null> {
    return this.graphApi.watchQuery<CpaasCustomer>({
      query: cpaasCustomerDetailsQuery,
      variables: {
        input: {
          customerId
        },
      },
      fetchPolicy: 'network-only' // note if change to cache-and-network, we need to handle an issue (see comment above)
    }).pipe(
      map((customer: CpaasCustomer | null) => {
        if (!customer) {
          return null;
        }

        return {
          ...customer,
          services: normalizePlatformServices(customer.services || [])
        }
      })
    );
  }

  public cpaasCustomerDetailsForOrderAccountInfo(orderAccountInfo: TnOrderAccountInfo): Observable<CpaasCustomer | null> {
    return this.cpaasCustomerDetails(orderAccountInfo.customerId || orderAccountInfo.accountId);
  }

  public cpaasTnPortalType(): TnPortalType {
    return TnPortalType.NUSO;
  }

  public currentCpaasCustomerSummary(): Observable<CpaasCustomerSummary | null> {
    return this.servicesService.currentCustomer().pipe(
      filter(isDefined),
      map(customer => ({
        id: customer.id,
        name: customer.name,
        level: 3
      })),
    )
  }

}
